import { isAxiosError } from "axios";
import { ChevronDown, SearchX } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

import { useLibrarySearch } from "../hooks/useResearch";
import { queryTerms } from "../readerText";
import type { ResearchFilters } from "../routes";
import { sourceById } from "../sources";
import type { LibraryHit, ResearchSourceId } from "../types";
import { ResultRow } from "./ResultRow";

const PAGE = 10;
const MAX = 25;

export const errorText = (error: unknown): string =>
  (isAxiosError<{ error?: string }>(error) && error.response?.data?.error) ||
  "The library didn't answer. Try again in a moment.";

export const ResultSkeletons: FC<{ rows?: number }> = ({ rows = 4 }) => (
  <ul className="divide-y divide-line-1" aria-busy>
    {Array.from({ length: rows }, (_, i) => (
      <li key={i} className="flex flex-col gap-2 px-4 py-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-full" />
      </li>
    ))}
  </ul>
);

type Props = {
  source: ResearchSourceId;
  query: string;
  filters: ResearchFilters;
  activeId: string | null;
  onOpen: (hit: LibraryHit) => void;
};

/**
 * One source's results, with what they are worth and a Show More. Keyed by
 * its source, query and filters, so a new search starts from the first page.
 */
export const SourceResults: FC<Props> = ({
  source,
  query,
  filters,
  activeId,
  onOpen
}) => {
  const spec = sourceById(source);
  const [limit, setLimit] = useState(PAGE);
  const search = useLibrarySearch(source, query, { limit, filters });
  const terms = queryTerms(query);
  const results = search.data?.results ?? [];

  return (
    <section
      className="flex flex-col"
      data-testid={`research-source-results-${source}`}
    >
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 text-sm text-fg-3">
        <span className="min-w-0 flex-1">{spec.authority}</span>
        {search.isFetching ? <Spinner className="size-3.5" /> : null}
      </div>
      {search.data?.note ? (
        <p className="mx-4 mb-2 rounded-md bg-warn-soft px-2 py-1.5 text-sm text-warn">
          {search.data.note}
        </p>
      ) : null}
      {search.isError ? (
        <EmptyState
          icon={SearchX}
          title="Search Didn't Go Through"
          description={errorText(search.error)}
          action={
            <Button size="sm" onClick={() => void search.refetch()}>
              Try Again
            </Button>
          }
          className="py-12"
        />
      ) : search.isPending ? (
        <ResultSkeletons rows={6} />
      ) : results.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title="Nothing Found"
          description={
            query
              ? `${spec.label} has nothing for “${query}”. Try fewer words, a citation, or All Sources.`
              : `${spec.label} has nothing to show yet.`
          }
          className="py-12"
        />
      ) : (
        <>
          <ul className="divide-y divide-line-1" data-testid="research-results">
            {results.map((hit, i) => (
              <ResultRow
                key={hit.id ?? `${hit.title}-${i}`}
                hit={hit}
                terms={terms}
                active={Boolean(hit.id) && hit.id === activeId}
                onOpen={onOpen}
              />
            ))}
          </ul>
          {search.data?.more && limit < MAX ? (
            <div className="flex justify-center px-4 py-3">
              <Button
                size="sm"
                variant="ghost"
                disabled={search.isFetching}
                onClick={() => setLimit((n) => Math.min(MAX, n + PAGE))}
                data-testid="research-more"
              >
                <ChevronDown />
                Show More
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
